'use client';

import { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';
import { useAppStore } from '@/stores/appStore';
import { useOverseasRestricted } from '@/hooks/useRestrictedMode';
import { useWorldCupStore } from '@/stores/worldCupStore';
import { useGateQueue } from '@/stores/gateQueueStore';
import { useStackNavigation } from '@/hooks/useStackNavigation';
import { useWebviewBridge } from '@/hooks/useWebviewBridge';
import { useLoginModalStore } from '@/stores/loginModalStore';
import {
  getWorldCupCharacters,
  submitWorldCupResult,
  getWorldCupGreetings,
  migrateWorldCupGuest,
  getWorldCupStatus,
} from '@/api/feedApi';
import { track } from '@/utils/analytics';
import { setUserPropertyOnce } from '@/utils/amplitude';
import { USER_PROPERTY_ONCE } from '@/utils/amplitudeEvents';
import MuseWorldCup from './MuseWorldCup';

const WORLDCUP_DONE_KEY = 'museling_worldcup_done';
const GUEST_SESSION_KEY = 'museling_guest_session_id';
const SELECTED_CHARACTER_KEY = 'museling_worldcup_character';
/** 크리에이터 랜딩(apply.museling.me) "둘러보기" 유입 — 이 탭 세션에서만 월드컵을 띄우지 않는다 (done 기록은 남기지 않음) */
const CREATOR_VISIT_SUPPRESS_KEY = 'museling_worldcup_suppress';
/** 비로그인 채팅 체험에서 사용 중인 캐릭터 — 가입 후 자연 복귀에 사용 */
const GUEST_CHAT_CHARACTER_KEY = 'museling_guest_chat_character';

export default function WorldCupGate() {
  const isLoggedIn = useAuthStore((s) => s.isLoggedIn);
  const isAuthInitialized = useAuthStore((s) => s.isAuthInitialized);
  const isTestAccount = useAuthStore((s) => s.user?.isTestAccount === true);
  // 로그인 직후 fetchMe 완료 전 user=null 상태에서 isTestAccount가 잠깐 false로 보여
  // 가계정도 월드컵이 깜빡 뜨는 문제 방지 — user 로딩 중엔 게이트 판정 보류.
  const isUserLoaded = useAuthStore((s) => s.user !== null);
  const isOverseasRestricted = useOverseasRestricted();
  const isInApp = useAppStore((s) => s.isInApp);

  const phase = useWorldCupStore((s) => s.phase);
  const setPhase = useWorldCupStore((s) => s.setPhase);
  const finalWinner = useWorldCupStore((s) => s.finalWinner);
  const rounds = useWorldCupStore((s) => s.rounds);
  const guestSessionId = useWorldCupStore((s) => s.guestSessionId);
  const setGuestSessionId = useWorldCupStore((s) => s.setGuestSessionId);
  const reset = useWorldCupStore((s) => s.reset);

  const { push } = useStackNavigation();
  const { sendToApp } = useWebviewBridge();
  const qc = useQueryClient();

  const [characters, setCharacters] = useState<any[]>([]);
  const [greetings, setGreetings] = useState<Record<string, any>>({});
  const [ready, setReady] = useState(false);

  // 로그인 후 게스트 세션 마이그레이션
  // - 월드컵 경로: SELECTED_CHARACTER_KEY 사용
  // - 둘러보기/채팅 직접 진입 경로: GUEST_CHAT_CHARACTER_KEY 사용
  // 둘 중 하나만 있으면 그걸로 chat 복귀. 둘 다 없으면 push 없이 캐릭터만 매칭.
  useEffect(() => {
    if (!isLoggedIn || !isAuthInitialized) return;
    const savedSessionId = localStorage.getItem(GUEST_SESSION_KEY);
    const worldcupCharId = localStorage.getItem(SELECTED_CHARACTER_KEY);
    const chatCharId = localStorage.getItem(GUEST_CHAT_CHARACTER_KEY);
    const targetCharId = worldcupCharId || chatCharId;
    if (!savedSessionId) return;

    migrateWorldCupGuest(savedSessionId)
      .then(async (res) => {
        track('guest_migrated', {
          character_id: res?.characterId ?? targetCharId,
          migrated_message_count: res?.migratedMessageCount ?? 0,
          already_matched: !!res?.alreadyMatched,
        });
        // 마이그레이션 완료 직후 — 빈 결과가 캐시될 수 있으므로 명시적으로 refetch.
        // ChatContent / ChatList 가 마이그레이션된 메시지를 즉시 보여줘야 함.
        if (res?.characterId) {
          await Promise.all([
            qc.invalidateQueries({ queryKey: ['messages', res.characterId] }),
            qc.invalidateQueries({ queryKey: ['character-relation', res.characterId] }),
            // 채팅방의 아이템 시트 — 가입 보상으로 받은 아이템이 즉시 보이도록
            qc.invalidateQueries({ queryKey: ['chat-room-items', res.characterId] }),
          ]);
        }
        // 가입 보상 다이아 50 + 가입 선물 아이템이 허브/채팅방/마이페이지에 즉시 반영되도록
        await Promise.all([
          qc.invalidateQueries({ queryKey: ['balance'] }),
          qc.invalidateQueries({ queryKey: ['inventory'] }),
          qc.invalidateQueries({ queryKey: ['chat-list'] }),
          // 가입 첫날 출석 상태 (백엔드가 checkedToday=true 반환)
          qc.invalidateQueries({ queryKey: ['attendance-status'] }),
          qc.invalidateQueries({ queryKey: ['attendance'] }),
        ]);
        localStorage.removeItem(GUEST_SESSION_KEY);
        localStorage.removeItem(SELECTED_CHARACTER_KEY);
        localStorage.removeItem(GUEST_CHAT_CHARACTER_KEY);
        if (targetCharId && typeof window !== 'undefined') {
          // 현재 이미 /chat/[targetCharId] 위에 있으면 push 안 함 — 같은 라우트 재 push 회피
          const here = window.location?.pathname || '';
          if (!here.includes(`/chat/${targetCharId}`)) {
            push(`/chat/${targetCharId}`);
          }
        }
      })
      .catch(() => {
        localStorage.removeItem(GUEST_SESSION_KEY);
        localStorage.removeItem(SELECTED_CHARACTER_KEY);
        localStorage.removeItem(GUEST_CHAT_CHARACTER_KEY);
      });
  }, [isLoggedIn, isAuthInitialized, push, qc]);

  useEffect(() => {
    if (!isAuthInitialized) return;
    // 로그인됐는데 user 객체 아직 안 올라왔으면 판정 보류 — isTestAccount 가
    // false→true 로 뒤늦게 뒤집히면서 월드컵이 깜빡 뜨는 걸 막는다.
    if (isLoggedIn && !isUserLoaded) return;

    // 크리에이터 랜딩에서 "뮤즈링 둘러보기"로 넘어온 방문(?from=creator)은 이 탭에서만 월드컵 스킵.
    // localStorage done 은 건드리지 않아서, 나중에 일반 유입으로 다시 오면 정상적으로 뜬다.
    if (typeof window !== 'undefined') {
      try {
        if (new URLSearchParams(window.location.search).get('from') === 'creator') {
          sessionStorage.setItem(CREATOR_VISIT_SUPPRESS_KEY, '1');
        }
        if (sessionStorage.getItem(CREATOR_VISIT_SUPPRESS_KEY) === '1') {
          setReady(true);
          return;
        }
      } catch {
        /* sessionStorage 접근 불가 환경이면 기존 흐름 그대로 */
      }
    }

    // 심사용 테스트 계정 또는 해외 Apple 앱 유저는 이상형 월드컵 스킵
    // localStorage에도 done 표시 박아서 향후 재마운트에도 안 뜨게 한다.
    if (isTestAccount || isOverseasRestricted) {
      setPhase('done');
      setReady(true);
      if (typeof window !== 'undefined') {
        localStorage.setItem(WORLDCUP_DONE_KEY, 'true');
      }
      return;
    }

    let savedSessionId: string | null = null;
    if (typeof window !== 'undefined') {
      const done = localStorage.getItem(WORLDCUP_DONE_KEY);
      if (done === 'true') {
        setReady(true);
        return;
      }
      savedSessionId = localStorage.getItem(GUEST_SESSION_KEY);
      if (savedSessionId) {
        setGuestSessionId(savedSessionId);
      }
    }

    // localStorage 의 done 캐시가 비었으면 → 백엔드 status 가 진실값.
    // 같은 사람이 다른 브라우저/디바이스/앱웹뷰로 들어와서 또 뜨던 버그 해결.
    // 로그인 유저 또는 게스트 세션이 있을 때만 의미 있다 (백엔드가 판별 가능한 케이스).
    let cancelled = false;

    const markDoneAndPersist = () => {
      setPhase('done');
      if (typeof window !== 'undefined') {
        try { localStorage.setItem(WORLDCUP_DONE_KEY, 'true'); } catch { /* ignore */ }
      }
      // 앱 웹뷰면 AsyncStorage 에도 같이 박아둔다 — 다음 부팅에 localStorage 가
      // evict 돼도 webviewOpened.worldcupDone 으로 자동 복원되니까 API 안 불러도 됨.
      if (isInApp) {
        try { sendToApp('worldcupDoneChanged', null); } catch { /* ignore */ }
      }
      setReady(true);
    };

    const loadAndStartWorldcup = async () => {
      try {
        const data = await getWorldCupCharacters();
        if (cancelled) return;
        if (data.characters && data.characters.length >= 2) {
          setCharacters(data.characters);
          setPhase('intro');
          try {
            const greetingData = await getWorldCupGreetings();
            if (greetingData && !cancelled) setGreetings(greetingData);
          } catch {}
        }
      } catch (err) {
        console.error('[WorldCupGate] 캐릭터 로드 실패:', err);
      } finally {
        if (!cancelled) setReady(true);
      }
    };

    (async () => {
      // 1) 백엔드 진실값 확인 — 가능한 경우에만.
      if (isLoggedIn || savedSessionId) {
        try {
          const status = await getWorldCupStatus(savedSessionId ?? undefined);
          if (cancelled) return;
          if (status?.isCompleted) {
            markDoneAndPersist();
            return;
          }
        } catch (err) {
          // 백엔드 실패 시엔 아래 fallback 로직대로 — UX 가 깨지지 않게.
          console.warn('[WorldCupGate] status 조회 실패, fallback:', err);
          if (cancelled) return;
        }
      }

      // 2) 아직 안 한 케이스 — 정책 분기:
      //    웹 + 로그인 유저는 월드컵 스킵 (기존 정책).
      //    그 외 (앱 웹뷰 로그인 / 비로그인) 는 진행.
      if (isLoggedIn && !isInApp) {
        setReady(true);
        return;
      }
      await loadAndStartWorldcup();
    })();

    return () => { cancelled = true; };
  }, [isAuthInitialized, isLoggedIn, isUserLoaded, isInApp, isTestAccount, isOverseasRestricted, setPhase, setGuestSessionId, sendToApp]);

  const handleComplete = useCallback(
    async (characterId: string) => {
      try {
        const result = await submitWorldCupResult({
          guestSessionId: guestSessionId ?? undefined,
          selectedCharacterId: characterId,
          rounds,
        });

        if (result?.guestSessionId) {
          setGuestSessionId(result.guestSessionId);
          localStorage.setItem(GUEST_SESSION_KEY, result.guestSessionId);
        }
      } catch (err) {
        console.error('[WorldCupGate] 결과 저장 실패:', err);
      }

      // setOnce: 가입 전 winner 가 가입 후에도 user property 로 보존됨 → cohort 분석 가능
      setUserPropertyOnce(
        USER_PROPERTY_ONCE.FIRST_WORLDCUP_WINNER_MUSE_ID,
        characterId,
      );
      track('worldcup_finished', {
        winner_muse_id: characterId,
        round_count: rounds.length,
        is_logged_in: isLoggedIn,
      });
      track('worldcup_signup_cta_clicked', { winner_muse_id: characterId });

      localStorage.setItem(WORLDCUP_DONE_KEY, 'true');
      localStorage.setItem(SELECTED_CHARACTER_KEY, characterId);
      if (isInApp) {
        try { sendToApp('worldcupDoneChanged', null); } catch { /* ignore */ }
      }
      setPhase('done');
      reset();

      push(`/chat/${characterId}`);
    },
    [guestSessionId, rounds, setPhase, setGuestSessionId, reset, push, isLoggedIn, isInApp, sendToApp],
  );

  const handleSkipReveal = useCallback(
    async (characterId: string) => {
      try {
        const result = await submitWorldCupResult({
          guestSessionId: guestSessionId ?? undefined,
          selectedCharacterId: characterId,
          rounds,
        });

        if (result?.guestSessionId) {
          setGuestSessionId(result.guestSessionId);
          localStorage.setItem(GUEST_SESSION_KEY, result.guestSessionId);
        }
      } catch (err) {
        console.error('[WorldCupGate] 결과 저장 실패:', err);
      }

      setUserPropertyOnce(
        USER_PROPERTY_ONCE.FIRST_WORLDCUP_WINNER_MUSE_ID,
        characterId,
      );
      track('worldcup_finished', {
        winner_muse_id: characterId,
        round_count: rounds.length,
        is_logged_in: isLoggedIn,
        skipped_chat_cta: true,
      });

      localStorage.setItem(WORLDCUP_DONE_KEY, 'true');
      if (isInApp) {
        try { sendToApp('worldcupDoneChanged', null); } catch { /* ignore */ }
      }
      setPhase('done');
      reset();
    },
    [guestSessionId, rounds, setPhase, setGuestSessionId, reset, isLoggedIn, isInApp, sendToApp],
  );

  const handleDismiss = useCallback(() => {
    track('worldcup_abandoned', { phase });
    localStorage.setItem(WORLDCUP_DONE_KEY, 'true');
    if (isInApp) {
      try { sendToApp('worldcupDoneChanged', null); } catch { /* ignore */ }
    }
    setPhase('done');
    reset();
  }, [setPhase, reset, phase, isInApp, sendToApp]);

  const wantsShow = ready && phase !== 'idle' && phase !== 'done';

  // 알림 안내·AI 고지 등 다른 첫 실행 모달과 겹치지 않게 순차 노출 (gateQueue)
  const gateActive = useGateQueue((s) => s.active);
  const requestGate = useGateQueue((s) => s.request);
  const releaseGate = useGateQueue((s) => s.release);

  useEffect(() => {
    if (!wantsShow) return;
    requestGate('worldcup');
    return () => releaseGate('worldcup');
  }, [wantsShow, requestGate, releaseGate]);

  if (!wantsShow) return null;
  if (gateActive !== 'worldcup') return null;

  return (
    <MuseWorldCup
      characters={characters}
      greetings={greetings}
      onComplete={handleComplete}
      onSkipReveal={handleSkipReveal}
      onDismiss={handleDismiss}
    />
  );
}
